<template>
  <v-form ref="form" :disabled="loading" :loading="loading" @submit.prevent="save">
    <v-text-field
      v-model.trim="name"
      :label="t('supervisorOrgSettings.account.name')"
    ></v-text-field>
    <v-file-input
      v-model="file"
      variant="outlined"
      prepend-inner-icon="$file"
      :prepend-icon="''"
      accept="image/png, image/jpeg"
      :label="t('supervisorOrgSettings.account.avatar')"
      :rules="[maxFileSize(maxFileSizeMB)]"
      hide-details="auto"
    >
      <template #append>
        <v-switch
          v-model="avatarPadding"
          color="primary"
          :label="t('supervisorOrgSettings.account.avatarPadding')"
          inline
          class="mt-2"
          hide-details
          size="small"
        ></v-switch>
      </template>
    </v-file-input>

    <v-radio-group
      v-model="lang"
      color="primary"
      :label="t('supervisorOrgSettings.language')"
      inline
      class="mt-2 -ml-3"
      hide-details
    >
      <v-radio
        v-for="locale in locales"
        :key="locale"
        :label="t(`locales.${locale}`)"
        :value="locale"
      ></v-radio>
    </v-radio-group>

    <v-btn type="submit" class="mt-5" color="success" :disabled="loading" :loading="loading">{{
      t("supervisorOrgSettings.account.save")
    }}</v-btn>
  </v-form>
</template>

<script setup lang="ts">
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { useSupervisorOrg } from "@/composables/useSupervisorOrg";
import { useToast, useUserStore } from "@/store";
import { locales, type Locale } from "@aircap/common/models";
import { getAvatarOriginalFilePath } from "@aircap/common/storage";
import { captureException } from "@sentry/vue";
import { updateDoc } from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { computed, ref, watch } from "vue";
import { useFirebaseStorage, useStorageFile } from "vuefire";
import { VForm } from "vuetify/components";

const maxFileSizeMB = 10;
const toast = useToast();
const { t } = useI18n();
const storage = useFirebaseStorage();
const user = useUserStore();
const { maxFileSize } = useRules();

const saving = ref(false);
const name = ref("");
const file = ref<File | null>(null);
const lang = ref<Locale>("fr");
const avatar = computed(() =>
  user.supervisorOrgId
    ? storageRef(storage, getAvatarOriginalFilePath({ supervisorOrgId: user.supervisorOrgId }))
    : null,
);
const avatarPadding = ref(false);

const { upload } = useStorageFile(avatar);
const form = ref<InstanceType<typeof VForm> | null>(null);

const { supervisorOrgRef, supervisorOrg, loadingSupervisorOrg } = useSupervisorOrg();

const loading = computed(() => loadingSupervisorOrg.value || saving.value);

watch(supervisorOrg, (supervisorOrg) => {
  if (!supervisorOrg) return;

  name.value = supervisorOrg.name;
  lang.value = supervisorOrg.lang;
  avatarPadding.value = !!supervisorOrg.avatarPadding;
});

const save = async () => {
  if (!form.value) return;
  if (!supervisorOrgRef.value) return;

  const { valid } = await form.value.validate();
  if (!valid) return;

  try {
    console.log("Saving supervisor org settings...");
    saving.value = true;

    await updateDoc(supervisorOrgRef.value, {
      name: name.value,
      lang: lang.value,
      avatarPadding: avatarPadding.value,
    });

    if (file.value) {
      // Additional validation to prevent bypass
      if (file.value.size > maxFileSizeMB * 1024 * 1024) {
        return;
      }
      await upload(file.value);
      user.resetKeyAvatar++;
    }

    toast.showSuccessToast();
  } catch (cause) {
    const error = new Error("Error updating supervisor org", { cause });
    console.error(error);
    captureException(error);
    toast.showErrorToast();
  } finally {
    saving.value = false;
  }
};
</script>
